import { useRef } from "react";

import { Icon, Spinner, TapTarget } from "../../ui";

interface ChipsInputProps {
  chips: string[];
  draft: string;
  onDraftChange: (value: string) => void;
  onAdd: () => void;
  onRemove: (chip: string) => void;
  onRemoveLast: () => void;
  placeholder: string;
  addButtonLabel: string;
  removeLabel: (chip: string) => string;
  disabled?: boolean;
  loading?: boolean;
}

/**
 * Free-form chips input. Reused by the CategoryFormDialog (for match
 * patterns) and the PatternChipRow row-edit affordance.
 *
 * Keyboard model:
 *   - Enter commits the draft as a new chip
 *   - Backspace on an empty draft removes the last chip
 *   - Clicking inside the wrapper focuses the input — matches the
 *     ergonomics of most "tags input" UIs.
 */
export function ChipsInput({
  chips,
  draft,
  onDraftChange,
  onAdd,
  onRemove,
  onRemoveLast,
  placeholder,
  addButtonLabel,
  removeLabel,
  disabled,
  loading,
}: ChipsInputProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const focusInput = () => inputRef.current?.focus();

  return (
    <div
      onClick={focusInput}
      onKeyDown={(e) => {
        if (e.target === e.currentTarget && e.key === "Enter") focusInput();
      }}
      role="presentation"
      className="flex min-h-11 cursor-text flex-wrap items-center gap-1.5 rounded-md border border-ink-200 bg-surface px-3 py-2 focus-within:border-accent-500 focus-within:ring-2 focus-within:ring-accent-500/20"
    >
      {chips.map((chip) => (
        <span
          key={chip}
          className="inline-flex items-center gap-1 rounded-full bg-ink-100 py-1 pr-1 pl-2.5 text-ink-700 text-xs"
        >
          {chip}
          <TapTarget
            aria-label={removeLabel(chip)}
            disabled={disabled}
            onClick={(e) => {
              e.stopPropagation();
              onRemove(chip);
            }}
            className="inline-flex h-5 w-5 items-center justify-center rounded-full text-ink-500 hover:bg-ink-200 hover:text-ink-900"
          >
            <Icon name="x" size="xs" />
          </TapTarget>
        </span>
      ))}
      <input
        ref={inputRef}
        value={draft}
        disabled={disabled}
        onChange={(e) => onDraftChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            onAdd();
          } else if (e.key === "Backspace" && draft === "" && chips.length > 0) {
            e.preventDefault();
            onRemoveLast();
          }
        }}
        onBlur={onAdd}
        placeholder={placeholder}
        className="min-w-[10rem] flex-1 bg-transparent text-ink-900 text-sm placeholder:text-ink-400 focus:outline-none disabled:opacity-50"
      />
      {draft.trim().length > 0 && (
        <TapTarget
          disabled={disabled}
          onMouseDown={(e) => e.preventDefault()}
          onClick={(e) => {
            e.stopPropagation();
            onAdd();
          }}
          className="rounded-md px-2 py-1 font-medium text-accent-700 text-xs hover:bg-accent-50 hover:text-accent-900 dark:text-accent-300 dark:hover:bg-accent-900/30 dark:hover:text-accent-200"
        >
          {addButtonLabel}
        </TapTarget>
      )}
      {loading && (
        <span className="inline-flex items-center px-1" aria-hidden>
          <Spinner size="xs" />
        </span>
      )}
    </div>
  );
}
